import React, { useEffect, useState } from 'react';
import {
  Badge,
  Tooltip,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Filled,
  ErrorCircle20Filled,
  Circle20Regular,
} from '@fluentui/react-icons';
import { apiClient } from '@/services/api';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  badge: {
    cursor: 'pointer',
  },
});

type ApiConnectionStatus = 'connected' | 'disconnected' | 'checking';

export const ApiStatus: React.FC = () => {
  const classes = useStyles();
  const [status, setStatus] = useState<ApiConnectionStatus>('checking');
  const [apiUrl, setApiUrl] = useState<string>('');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkConnection = async () => {
    setStatus('checking');
    setError(null);

    try {
      // Get the actual API URL being used
      const baseUrl = apiClient.defaults.baseURL || '/api';
      setApiUrl(baseUrl);

      // Try to hit the health endpoint
      const response = await apiClient.get('/health');

      if (response.status === 200) {
        setStatus('connected');
        setLastChecked(new Date());
      } else {
        setStatus('disconnected');
        setError(`Unexpected status: ${response.status}`);
      }
    } catch (err) {
      setStatus('disconnected');
      setError(err instanceof Error ? err.message : 'Connection failed');
      setLastChecked(new Date());
    }
  };

  useEffect(() => {
    // Check connection on mount
    checkConnection();

    // Check every 30 seconds
    const interval = setInterval(checkConnection, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = () => {
    switch (status) {
      case 'connected':
        return <CheckmarkCircle20Filled style={{ color: '#107C10' }} />;
      case 'disconnected':
        return <ErrorCircle20Filled style={{ color: '#D83B01' }} />;
      case 'checking':
        return <Circle20Regular style={{ color: '#0078D4' }} />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connected':
        return 'API Connected';
      case 'disconnected':
        return 'API Disconnected';
      case 'checking':
        return 'Checking API...';
    }
  };

  const getTooltipContent = () => {
    const lines = [
      `Status: ${getStatusText()}`,
      `API URL: ${apiUrl || 'Not configured'}`,
    ];

    if (lastChecked) {
      lines.push(`Last checked: ${lastChecked.toLocaleTimeString()}`);
    }

    if (error) {
      lines.push(`Error: ${error}`);
    }

    return lines.join('\n');
  };

  return (
    <Tooltip content={<pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '12px' }}>{getTooltipContent()}</pre>} relationship="description">
      <div className={classes.container} onClick={checkConnection}>
        {getStatusIcon()}
        <Badge
          appearance={status === 'connected' ? 'tint' : 'ghost'}
          color={status === 'connected' ? 'success' : status === 'disconnected' ? 'danger' : 'informative'}
          className={classes.badge}
        >
          {getStatusText()}
        </Badge>
      </div>
    </Tooltip>
  );
};
